<template>
  <FilterDropdown
    ref="filterDropdown"
    :placeholder="inputPlaceholder"
    :hide-filter-popup="hideFilterPopup"
    :search-results="searchResults"
    :show-shortcut-key-pill="true"
    :shortcut="{ ...key, shortcutKey: 'F' }"
    :on-item-click="selectSearchResult"
    :on-item-right-side-click="itemRightSideClick"
    :select-only-search-result="selectOnlySearchResult"
    :should-use-query="shouldUseQuery"
    :selected-filter-type="selectedFilterType"
    :filters="currentFilters"
    :hover-preview-disabled="hoverPreviewDisabled"
    @query-change="searchValue = $event"
    @esc="escHandler"
  />
</template>

<script>
import filterMembers from '@/utilities/filterMembers';
import styleVariables from '@/assets/scss/variables.module.scss';
import EventBus from '@/utilities/eventBus';
import getMemberLabel from '@/utilities/getMemberLabel';
import cardStatuses from '@/mixins/cardStatuses';
import cardRelationStatuses from '@/mixins/cardRelationStatus';
import viewFiltersMixin from '@/mixins/viewFiltersMixin';
import defaultShortcutKeyPillStyle from '@/mixins/defaultShortcutKeyPillStyle';
import estimateOptions from '@/mixins/estimateOptions';
import FilterDropdown from './FilterDropdown.vue';
import usePriorityOptions from '@/utilities/composables/usePriorityOptions';
import Fuse from 'fuse.js';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import boardListMixin from '@/mixins/boardListMixin';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { useMyWorkMemberRoute } from '@/utilities/composables/useMyWorkMemberRoute';
import useEpicFeature from '@/utilities/composables/useEpicFeature';
import { TeamMemberRole } from '@superthread-com/api';

export default {
  name: 'FilterPopup',
  components: {
    FilterDropdown,
  },
  mixins: [
    cardStatuses,
    cardRelationStatuses,
    viewFiltersMixin,
    defaultShortcutKeyPillStyle,
    estimateOptions,
    boardListMixin,
  ],

  props: {
    preselectedType: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['hideFilterPopup'],

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { priorityOptions } = usePriorityOptions();
    const { isGuestReader } = useCurrentRole();
    const { isMyWorkOrMemberRoute, isMyWorkCreatedTab, isMyWorkAssignedTab, myWorkRouteUserId } =
      useMyWorkMemberRoute();
    const { epicUpgradeBadge, allowEpicInteraction } = useEpicFeature();
    return {
      priorityOptions,
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      isGuestReader,
      isMyWorkOrMemberRoute,
      isMyWorkCreatedTab,
      isMyWorkAssignedTab,
      myWorkRouteUserId,
      epicUpgradeBadge,
      allowEpicInteraction,
    };
  },

  data() {
    return {
      searchValue: '',
      debouncedSearchValue: '',
      selectedFilterType: '',
      newFilter: {
        include: {},
        exclude: {},
      },
      userCheckIcon: 'user-check',
      fuse: null,
      getEpics: [],
      debounceTimer: null,
    };
  },

  computed: {
    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.boardId;
      }
      return this.$store.getters.currentBoardId;
    },
    filterIconByIdMap() {
      const parentCardIcon = 'parent-card';
      return {
        has_members: 'avatar',
        [this.currentUser.id]: 'checkbox',
        projects: 'folder',
        boards: 'board',
        statuses: 'circle',
        title: 'type',
        members: this.userCheckIcon,
        owners: 'user',
        creators: 'user',
        tags: 'tag',
        epics: 'map',
        parent_cards: parentCardIcon,
        is_archived: 'archive',
        is_parent: parentCardIcon,
        is_child: 'card-child',
        linked_cards: 'flag',
        due_date: 'calendar',
        time_created: 'calendar',
        time_updated: 'calendar',
        external_links: 'link-st',
        priority: 'chevrons-up',
        estimates: this.estimateDefaultIcon.src,
      };
    },
    filterTypes() {
      const getIconAndCustomIconById = (id) => ({
        icon: this.filterIconByIdMap[id],
        customIcon: this.filterIconByIdMap[id],
      });
      const getIdAndIcon = (id) => ({
        id,
        key: id,
        ...getIconAndCustomIconById(id),
      });
      const filterTypes =
        // support showing assignee filter for my-work/created cards tab
        !this.isMyWorkOrMemberRoute || (this.isMyWorkCreatedTab && this.isMyWorkOrMemberRoute)
          ? [
              {
                customIconSize: 24,
                customIconColor: styleVariables.gray400,
                ...getIdAndIcon('has_members'),
                label: this.translate('noassignee'),
                checkbox: true,
                checked: this.currentFilters?.has_members !== undefined,
                lighter: true,
                strong: true,
              },
              {
                ...getIconAndCustomIconById(this.currentUser.id),
                key: 'currentuser',
                label: this.userDisplayName,
                checkbox: true,
                checked:
                  this.currentFilters.exclude?.members?.length > 0
                    ? this.currentFilters.exclude?.members?.includes(this.currentUser.id)
                    : this.currentFilters.include?.members?.includes(this.currentUser.id),
                lighter: true,
                strong: true,
                avatar: true,
                id: this.currentUser.id,
                separator: true,
              },
            ]
          : [];
      if (this.isViewOpen || this.inEpicBoard) {
        filterTypes.push(
          {
            ...getIdAndIcon('projects'),
            label: this.translate('space'),
            category: true,
          },
          {
            ...getIdAndIcon('boards'),
            label: this.translate('boards'),
            category: true,
          }
        );
      }
      filterTypes.push(
        {
          ...getIdAndIcon('statuses'),
          label: this.translate('status'),
          category: true,
        },
        {
          ...getIdAndIcon('title'),
          label: this.translate('title'),
          category: true,
        },
        // support showing assignee filter for my-work/created cards tab
        ...(!this.isMyWorkOrMemberRoute || (this.isMyWorkCreatedTab && this.isMyWorkOrMemberRoute)
          ? [
              {
                ...getIdAndIcon('members'),
                label: this.translate('assignees'),
                category: true,
              },
            ]
          : []),
        {
          ...getIdAndIcon('owners'),
          label: this.translate('owner'),
          category: true,
        },
        // support showing creator filter for my-work/assigned tab
        ...(!this.isMyWorkOrMemberRoute || (this.isMyWorkAssignedTab && this.isMyWorkOrMemberRoute)
          ? [
              {
                ...getIdAndIcon('creators'),
                label: this.translate('creator'),
                category: true,
              },
            ]
          : []),
        {
          ...getIdAndIcon('tags'),
          synonyms: ['labels'],
          label: this.translate('tags'),
          category: true,
        }
      );
      if (!this.inEpicBoard && this.currentRouteName !== this.$constants.epicsBoardRoute.name) {
        filterTypes.push(
          ...(!this.isGuestReader
            ? [
                {
                  ...getIdAndIcon('epics'),
                  label: `${this.translate('projectIs')}`,
                  category: true,
                  ...this.epicUpgradeBadge,
                  disabledCallback: () => {
                    this.hideFilterPopup();
                    this.allowEpicInteraction();
                  },
                },
              ]
            : []),
          {
            ...getIdAndIcon('parent_cards'),
            label: `${this.translate('parentCard')}...`,
            category: true,
          }
        );
      }
      filterTypes.push(
        ...(this.isMyWorkOrMemberRoute || this.isViewOpen
          ? [
              {
                ...getIdAndIcon('is_archived'),
                label: this.translate('cardIsArchived'),
                category: true,
              },
            ]
          : []),
        {
          ...getIdAndIcon('is_parent'),
          label: this.translate('cardIsParent'),
          category: true,
        },
        {
          ...getIdAndIcon('is_child'),
          label: this.translate('cardIsChild'),
          category: true,
        },
        {
          ...getIdAndIcon('linked_cards'),
          label: this.translate('cardIsRelated'),
          category: true,
        },
        {
          ...getIdAndIcon('due_date'),
          label: this.translate('dueDate'),
          category: true,
        },
        {
          ...getIdAndIcon('time_created'),
          label: this.translate('createdDate'),
          category: true,
        },
        {
          ...getIdAndIcon('time_updated'),
          label: this.translate('updatedDate'),
          category: true,
        },
        {
          ...getIdAndIcon('external_links'),
          label: this.translate('links'),
          category: true,
        },
        {
          ...getIdAndIcon('priority'),
          label: this.translate('priority'),
          category: true,
        },
        {
          ...getIdAndIcon('estimates'),
          label: this.translate('estimate'),
          category: true,
        }
      );
      return filterTypes;
    },
    currentUser() {
      return this.$store.getters.getUser;
    },

    userDisplayName() {
      return getMemberLabel(this.currentUser);
    },

    dueDateFilters() {
      const duedateOptions = [
        'now',
        'now+2d',
        'now+3d',
        'now+1w',
        'now+2w',
        'now+3w',
        'now+1M',
        'now+2M',
        'now+3M',
        'now+6M',
        'now+1y',
        '0',
      ];

      const overdueOrNoDueDate = {
        now: 'overdue',
        0: 'noduedate',
      };

      const labelFunction = (label) => overdueOrNoDueDate[label] || label;

      return this.mapDateFilters(duedateOptions, 'due_date', labelFunction);
    },

    createdDateFilters() {
      const createdOptions = [
        'now-1d',
        'now-2d',
        'now-3d',
        'now-1w',
        'now-2w',
        'now-3w',
        'now-1M',
        'now-2M',
        'now-3M',
        'now-6M',
        'now-1y',
      ];

      return this.mapDateFilters(createdOptions, 'time_created');
    },

    activityFilters() {
      const updatedOptions = [
        'now-1h',
        'now-4h',
        'now-1d',
        'now-2d',
        'now-3d',
        'now-1w',
        'now-2w',
        'now-3w',
        'now-1M',
        'now-2M',
        'now-3M',
        'now-6M',
        'now-1y',
      ];

      return this.mapDateFilters(updatedOptions, 'time_updated');
    },

    externalLinkOptions() {
      return [
        {
          key: 'external_links',
          id: 'github_pull_request',
          icon: 'github-icon',
          customIcon: true,
          label: this.translate('github_pull_request'),
          checkbox: true,
          checked:
            this.currentFilters?.include?.external_links?.includes('github_pull_request') ||
            this.currentFilters?.exclude?.external_links?.includes('github_pull_request'),
          lighter: true,
        },
      ];
    },

    externalLinksFilters() {
      const linkOptions = this.externalLinkOptions;
      if (!this.searchValue) {
        return linkOptions;
      }
      return linkOptions.filter((ft) =>
        ft.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },

    statusesFilters() {
      const key = this.selectedFilterType;
      const isEpic = this.currentBoardId === this.$constants.epicsBoardId;

      const statusesOptions = [
        ...this.defaultLists(isEpic).map((status) => ({
          customIcon: status.icon,
          icon: status.icon,
          id: status.id,
          key,
          label: status.label,
          customIconColor: status.color,
          checkbox: true,
          checked:
            (status.id === 'backlog' && this.newFilter.has_status !== undefined) ||
            (this.newFilter.include[key]?.length
              ? this.newFilter.include[key]?.includes(status.id)
              : this.newFilter.exclude[key]?.includes(status.id)),
          lighter: true,
        })),
      ];

      if (!this.searchValue) {
        return statusesOptions;
      }
      return statusesOptions.filter((st) =>
        st.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },

    mappedPriorityOptions() {
      return this.priorityOptions.map((priority) => ({
        customIcon: priority.icon,
        icon: priority.icon,
        id: priority.id,
        key: 'priority',
        label: this.translate(priority.label),
        customIconColor: priority.customIconColor,
        checkbox: true,
        checked: this.isChecked('priority', priority.id),
        lighter: true,
      }));
    },

    priorityItems() {
      if (!this.searchValue) {
        return this.mappedPriorityOptions;
      }
      return this.mappedPriorityOptions.filter((priority) =>
        priority.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },

    noEstimateOption() {
      return {
        id: 0,
        key: 'estimates',
        label: this.translate('noEstimate'),
        checkbox: true,
        checked: this.isChecked('estimates', 0),
        lighter: true,
      };
    },

    mappedEstimateOptions() {
      // estimate systems have same number of values so we can iterate all of them like this
      return this.$constants.estimateSystems.tshirt.values.map((value, index) => ({
        id: index + 1,
        key: 'estimates',
        label: this.generateEstimateLabelForFilters(index),
        checkbox: true,
        checked: this.isChecked('estimates', index + 1),
        lighter: true,
      }));
    },

    estimateItems() {
      const sortedEstimateOtions = [this.noEstimateOption, ...this.mappedEstimateOptions];
      if (!this.searchValue) {
        return sortedEstimateOtions;
      }
      return sortedEstimateOtions.filter((estimate) =>
        estimate.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },

    projectFilters() {
      const key = this.selectedFilterType;
      let projectsArray = [];

      if (this.inEpicBoard) {
        projectsArray = this.getProjectsForEpic();
      } else {
        projectsArray = this.teamProjects;
      }

      const projectOptions = [
        ...projectsArray
          .map((project) => ({
            customIcon: project?.icon?.src || '',
            icon: project?.icon?.src || '',
            id: project.id,
            key,
            label: project.title,
            customIconColor: project?.icon?.color || '',
            checkbox: true,
            checked: this.isChecked(key, project.id),
            lighter: true,
            iconType: project?.icon?.type || '',
            privateItem: project?.is_private,
          }))
          .sort((a, b) => b.checked - a.checked),
      ];

      if (!this.searchValue) {
        return projectOptions;
      }
      return projectOptions.filter((proj) =>
        proj.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },

    mappedCardRelationStatuses() {
      return Object.entries(this.cardRelationStatus).map((type) => ({
        customIcon: type[1].icon,
        icon: type[1].icon,
        id: type[0],
        key: 'linked_cards',
        label: type[1].label,
        checkbox: true,
        checked: this.isChecked('linked_cards', type[0]),
        lighter: true,
        customIconColor: type[1].color,
      }));
    },

    linkedCardFilters() {
      const linkedCardOptions = [...this.mappedCardRelationStatuses].sort(
        (a, b) => b.checked - a.checked
      );

      if (!this.searchValue) {
        return linkedCardOptions;
      }
      return linkedCardOptions.filter((type) =>
        type.label.toLowerCase().includes(this.searchValue.toLowerCase())
      );
    },

    boardFilters() {
      if (!this.teamProjects.length) return [];

      const key = this.selectedFilterType;
      let boardOptions = [];
      let displayedProjects = [];

      if (this.inEpicBoard) {
        const epicId = this.currentBoardId.split('-')[1] || '';
        if (!Object.values(this.$store.getters.getCardsByEpicId(epicId)).length) return [];
        displayedProjects = this.getProjectsForEpic();
      } else {
        displayedProjects = this.teamProjects;
      }

      boardOptions = displayedProjects.reduce((acc, project) => {
        let projectBoards = this.getProjectBoards(project.id);

        if (this.inEpicBoard) {
          const epicBoards = this.getBoardsOfEpic();
          if (epicBoards.size > 0) {
            // Only filter if epicBoards is not empty
            projectBoards = projectBoards.filter((board_id) => epicBoards.has(board_id));
          }
        }

        const options = projectBoards
          .map((board_id) => ({
            key,
            id: this.flatBoards[board_id].id,
            label: this.flatBoards[board_id].title,
            checkbox: true,
            checked: this.newFilter?.include[key]?.length
              ? this.newFilter?.include[key]?.includes(board_id)
              : this.newFilter?.exclude[key]?.includes(board_id),
            lighter: true,
            groupingItem: true,
            groupLabel: project.title,
            groupIcon: project?.icon?.src || '',
            groupIconColor: project?.icon?.color || '',
            groupIconType: project?.icon?.type || this.iconEnum,
            groupingItemId: project.id,
            groupPrivateItem: project?.is_private,
          }))
          .sort((a, b) => b.checked - a.checked);

        if (options.length > 0) {
          options.at(-1).separator = true;
        }

        return acc.concat(options);
      }, []);

      delete boardOptions.at(-1).separator;

      if (!this.searchValue) {
        return boardOptions;
      }

      return boardOptions
        .filter((board) => board.label.toLowerCase().includes(this.searchValue.toLowerCase()))
        .map((board, index, arr) => {
          // Fix separators to be before boards with project title
          const nextBoard = arr[index + 1];
          delete board.separator;
          if (nextBoard && board.groupingItemId !== nextBoard.groupingItemId) {
            board.separator = true;
          }
          return board;
        });
    },

    filteredFilters() {
      switch (this.selectedFilterType) {
        case 'subscribers':
        case 'creators':
        case 'owners':
        case 'editor':
        case 'commentors':
        case 'members':
          return this.filterMembersHandler(this.selectedFilterType);
        case 'due_date':
          return this.dueDateFilters;
        case 'time_created':
          return this.createdDateFilters;
        case 'time_updated':
          return this.activityFilters;
        case 'tags':
          return this.filterTagsHandler();
        case 'external_links':
          return this.externalLinksFilters;
        case 'statuses':
          return this.statusesFilters;
        case 'projects':
          return this.projectFilters;
        case 'boards':
          return this.boardFilters;
        case 'linked_cards':
          return this.linkedCardFilters;
        case 'priority':
          return this.priorityItems;
        case 'estimates':
          return this.estimateItems;
        case 'parent_cards':
          return this.parentCardFilter;
        case 'epics':
          return this.epicsFilter;
        case 'title':
          return [];
        default:
          return this.filterTypes;
      }
    },

    searchResults() {
      if (!this.selectedFilterType) {
        if (this.debouncedSearchValue) {
          return this.fuse.search(this.debouncedSearchValue).map((result) => result.item);
        }
        return this.filterTypes;
      }
      return this.filteredFilters;
    },

    unifiedResults() {
      const addProps = (items, labelKey, additionalProps = {}) =>
        items.map((item) => ({
          ...item,
          ...(labelKey && {
            breadcrumbLabel: this.$capitalizeFirstLetter(this.translate(labelKey)),
          }),
          ...additionalProps,
          separator: false,
          checkbox: false,
          groupingItem: false,
        }));

      const categories = this.filterTypes.filter(
        ({ key, id }) => key !== 'currentuser' && id !== 'has_members'
      );
      return [
        ...addProps(categories),
        ...(!this.isMyWorkOrMemberRoute || (this.isMyWorkCreatedTab && this.isMyWorkOrMemberRoute)
          ? addProps([this.noAssigneeItem], 'assignee')
          : []),
        ...(!this.isMyWorkOrMemberRoute || (this.isMyWorkCreatedTab && this.isMyWorkOrMemberRoute)
          ? addProps(this.mappedMemberItems, 'assignee', { key: 'members' })
          : []),
        ...addProps(this.mappedMemberItems, 'owner', { key: 'owners' }),
        ...(!this.isMyWorkOrMemberRoute || (this.isMyWorkAssignedTab && this.isMyWorkOrMemberRoute)
          ? addProps(this.mappedMemberItems, 'creator', { key: 'creators' })
          : []),
        ...addProps(this.mappedTags, 'tag'),
        ...addProps(this.mappedCardRelationStatuses),
        ...addProps(this.activityFilters, 'updatedDate'),
        ...addProps(this.createdDateFilters, 'createdDate'),
        ...addProps(this.dueDateFilters, 'dueDate'),
        ...addProps(this.externalLinkOptions, 'linkUrlLabel'),
        ...addProps(this.mappedPriorityOptions, 'priority'),
        ...addProps(this.mappedEstimateOptions, 'estimate'),
        ...addProps([this.noEstimateOption], 'estimate'),
        ...addProps(this.statusesFilters, 'status', { key: 'statuses' }),
        ...addProps(this.parentCardFilter, 'parentCard'),
        ...(!this.isGuestReader ? addProps(this.epicsFilter, 'epic') : []),
        ...(this.isViewOpen
          ? [
              ...addProps(this.projectFilters, 'space', { key: 'projects' }),
              ...addProps(this.boardFilters, 'board', { key: 'boards' }),
            ]
          : []),
      ];
    },

    teamMembers() {
      return Object.values(this.$store.getters.getTeamMembers);
    },

    teamProjects() {
      return this.$store.getters.getTeamProjects || [];
    },

    currentFilters() {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        return this.$store.getters.getViewFilters || this.newFilter;
      }

      return this.$store.getters.boardFilters(this.currentBoardId) || this.newFilter;
    },

    inputPlaceholder() {
      switch (this.selectedFilterType) {
        case '':
          return this.translate('filters___');
        case 'members':
          return `${this.$capitalizeFirstLetter(this.translate('assignees'))}...`;
        case 'creators':
          return `${this.$capitalizeFirstLetter(this.translate('creators'))}...`;
        case 'owners':
          return `${this.$capitalizeFirstLetter(this.translate('owners'))}...`;
        case 'tags':
          return `${this.$capitalizeFirstLetter(this.translate('tags'))}...`;
        case 'due_date':
          return `${this.$capitalizeFirstLetter(this.translate('dueDate'))}...`;
        case 'time_created':
          return `${this.$capitalizeFirstLetter(this.translate('created'))}...`;
        case 'external_links':
          return `${this.$capitalizeFirstLetter(this.translate('links'))}...`;
        case 'boards':
          return `${this.$capitalizeFirstLetter(this.translate('boards'))}...`;
        case 'projects':
          return `${this.$capitalizeFirstLetter(this.translate('spaces'))}...`;
        case 'statuses':
          return `${this.$capitalizeFirstLetter(this.translate('status'))}...`;
        case 'linked_cards':
          return `${this.$capitalizeFirstLetter(this.translate('related'))}...`;
        case 'parent_cards':
          return `${this.$capitalizeFirstLetter(this.translate('parentCard'))}...`;
        case 'epics':
          return `${this.$capitalizeFirstLetter(this.translate('epics'))}...`;
        case 'title':
          return `${this.$capitalizeFirstLetter(this.translate('title'))}...`;
        default:
          return this.translate('filters___');
      }
    },

    currentTeamId() {
      return this.$store.getters.getTeamID;
    },

    currentProjectId() {
      return this.$store.getters.getCurrentProjectId;
    },

    projectTags() {
      return this.$store.getters.getProjectTags(this.currentProjectId);
    },

    workspaceTags() {
      return this.$store.getters.getWorkspaceTags(this.currentTeamId);
    },

    projectWorkspaceTags() {
      return [...this.projectTags, ...this.workspaceTags];
    },

    flatBoards() {
      return this.$store.getters.getFlatBoards;
    },

    mappedMemberItems() {
      return this.teamMembers
        .filter((member) => member.role !== TeamMemberRole.GuestReader)
        .map((member) => ({
          customIcon: 'checkbox',
          icon: 'checkbox',
          key: 'members',
          label: getMemberLabel(member),
          checkbox: true,
          checked: this.isChecked('members', member.id),
          lighter: true,
          avatar: true,
          id: member.id,
          role: member.role,
          name: member.name,
          email: member.email,
          last_name: member.last_name,
          first_name: member.first_name,
          display_name: member.display_name,
        }));
    },

    noAssigneeItem() {
      return {
        customIcon: 'avatar',
        customIconSize: 24,
        customIconColor: styleVariables.gray400,
        icon: 'avatar',
        key: 'members',
        id: 'has_members',
        label: this.translate('noassignee'),
        checkbox: true,
        checked: this.newFilter?.has_members !== undefined,
        lighter: true,
        strong: true,
      };
    },

    mappedTags() {
      const key = 'tags';
      const tags =
        this.isViewOpen || this.inEpicBoard
          ? Object.values(this.$store.getters.getTags)
          : this.projectWorkspaceTags.map((t) => this.$store.getters.getTags[t]);

      return tags.map((tag) => ({
        customIcon: 'tag',
        icon: 'tag',
        key,
        label: tag.name,
        customIconColor: tag.color,
        checkbox: true,
        checked: this.isChecked(key, tag.id),
        lighter: true,
        avatar: false,
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
      }));
    },

    parentCardFilter() {
      let parentCardOptions = [];
      const parentCards = [];

      if (
        [this.$constants.routeNames.board, this.$constants.routeNames.sprint].includes(
          this.currentRouteName
        )
      ) {
        parentCards.push(...this.getParentCardsForBoard());
      } else {
        if (this.getSearchResults?.cards?.length) {
          this.getSearchResults.cards.forEach((card) => {
            parentCards.push(card);
          });
        }
      }

      parentCardOptions = parentCards.reduce((acc, card) => {
        const option = {
          customIcon: this.cardStatuses[card.status]?.icon || 'circle-dotted',
          icon: this.cardStatuses[card.status]?.icon || 'circle-dotted',
          id: card.card_id || card.id,
          key: 'parent_cards',
          label: card.title,
          customIconColor: card.list_color || this.cardStatuses[card.status]?.color,
          checkbox: true,
          checked: this.isChecked('parent_cards', card.card_id || card.id),
          lighter: true,
        };

        if (
          !this.searchValue ||
          option.label.toLowerCase().includes(this.searchValue.toLowerCase())
        ) {
          acc.push(option);
        }

        return acc;
      }, []);

      return parentCardOptions.sort((a, b) => {
        if (a.checked && !b.checked) return -1;
        if (!a.checked && b.checked) return 1;
        return a.label.localeCompare(b.label);
      });
    },

    currentRouteName() {
      return this.$store.getters.getCurrentRoute.name;
    },

    getSearchResults() {
      return this.$store.getters.searchResults;
    },

    shouldUseQuery() {
      return this.selectedFilterType === 'parent_cards';
    },

    epicsFilter() {
      let epicOptions = [];
      let displayedEpics = [];

      if (
        [this.$constants.routeNames.board, this.$constants.routeNames.sprint].includes(
          this.currentRouteName
        )
      ) {
        displayedEpics = this.getEpicsForBoard();
      } else {
        displayedEpics = this.getEpics;
      }

      epicOptions = displayedEpics?.reduce((acc, epic) => {
        const option = {
          customIcon: epic.icon?.src || this.$constants.epicDefaultIcon,
          icon: epic.icon?.src || this.$constants.epicDefaultIcon,
          id: epic.id,
          key: 'epics',
          label: epic.title,
          customIconColor: epic.icon?.color,
          checkbox: true,
          checked: this.isChecked('epics', epic.id),
          lighter: true,
          type: epic.icon?.type,
        };

        if (
          !this.searchValue ||
          option.label.toLowerCase().includes(this.searchValue.toLowerCase())
        ) {
          acc.push(option);
        }

        return acc;
      }, []);

      return epicOptions.sort((a, b) => {
        if (a.checked && !b.checked) return -1;
        if (!a.checked && b.checked) return 1;
        return a.label.localeCompare(b.label);
      });
    },
    hoverPreviewDisabled() {
      return !['subscribers', 'creators', 'owners', 'editor', 'commentors', 'members'].includes(
        this.selectedFilterType
      );
    },
  },

  watch: {
    selectedFilterType(newValue) {
      if (newValue) {
        this.searchValue = '';
        this.debouncedSearchValue = '';
        this.$refs.filterDropdown?.clearSearchValue?.();
        this.$refs.filterDropdown?.setSelectedIndex?.(0);
        if (this.multivalueFilterKey(newValue)) {
          this.$refs.filterDropdown?.setSelectedIndex?.(0);
        }
      }
    },
    searchValue(newValue, oldValue) {
      if (newValue.length || oldValue.length) {
        this.mergeFilters();
      }
      this.debounceSearch(newValue);
    },
    draggingIsOn(newValue) {
      if (newValue) {
        this.hideFilterPopup();
      }
    },
  },

  mounted() {
    if (this.preselectedType) {
      this.selectedFilterType = this.preselectedType;
    }

    this.mergeFilters();
    this.fetchAllTags();

    this.fuse = new Fuse(this.unifiedResults, {
      keys: [
        'label',
        'synonyms',
        'name',
        'slug',
        'display_name',
        'email',
        'first_name',
        'last_name',
      ],
      threshold: 0.3,
    });

    const epicBoard = this.$store.getters.getFlatBoards[this.$constants.epicsBoardId] || {};
    if (!this.$isBoardFullyLoaded(epicBoard)) {
      this.$store.dispatch('fetchBoard', { boardId: this.$constants.epicsBoardId }).then(() => {
        this.getEpics = Object.values(this.$store.getters.getEpics);
      });
    } else {
      this.getEpics = Object.values(this.$store.getters.getEpics);
    }
  },

  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },

  methods: {
    isChecked(key, id) {
      return this.newFilter.include[key]?.length
        ? this.newFilter.include[key]?.includes(id)
        : this.newFilter.exclude[key]?.includes(id);
    },
    fetchAllTags() {
      if (!this.isViewOpen) {
        return;
      }

      const projTags = this.$store.getters.projectTags;

      this.teamProjects.forEach((proj) => {
        if (!projTags[proj.id]) {
          this.$store.dispatch('fetchTags', { projectId: proj.id });
        }
      });
    },
    mergeFilters() {
      this.newFilter = {
        ...this.newFilter,
        ...this.currentFilters,
      };
    },

    filterMembersHandler(key) {
      const isMe = (member) => member.id === this.$store.getters.getUserId;
      const myWorkAssigned =
        key === 'members' && this.isMyWorkOrMemberRoute && this.isMyWorkAssignedTab;
      const myWorkCreated =
        key === 'creators' && this.isMyWorkOrMemberRoute && this.isMyWorkCreatedTab;
      const myWorkRouteUserId = this.myWorkRouteUserId;

      const isMemberDisabled = (member) => {
        // disabled:true should be applied only to the current user when:
        //   1. the filter is members and the current route is my-work/assigned
        //   2. the filter is creators and the current route is my-work/created
        if (myWorkRouteUserId) {
          return member.id === myWorkRouteUserId;
        }
        return isMe(member) && (myWorkAssigned || myWorkCreated);
      };

      const filtered = filterMembers(this.mappedMemberItems, this.searchValue).map((member) => ({
        ...(member ?? {}),
        checked: this.isChecked(key, member.id),
        key,
        ...(isMemberDisabled(member) ? { disabled: true } : {}),
      }));

      const sortedFiltered = [...filtered.sort((a, b) => b.checked - a.checked)];

      if (
        this.selectedFilterType === 'members' &&
        this.translate('noassignee').toLowerCase().includes(this.searchValue.toLowerCase()) &&
        !(this.isMyWorkOrMemberRoute && this.isMyWorkAssignedTab)
      ) {
        sortedFiltered.unshift(this.noAssigneeItem);
      }

      return sortedFiltered;
    },

    filterTagsHandler() {
      const filtered = this.filterTags(this.mappedTags, this.searchValue);
      const sortedFiltered = [
        ...filtered.sort((a, b) => {
          if (a.checked && !b.checked) return -1;
          if (!a.checked && b.checked) return 1;
          return a.label.localeCompare(b.label);
        }),
      ];
      return sortedFiltered;
    },

    filterTags(tags, searchValue = this.searchValue) {
      const queries = searchValue.toLowerCase().split(' ');
      if (!searchValue) return tags;

      return tags.filter((m) =>
        queries.some((q) => {
          if (!q) return false;
          const name = (m.name || '').toLowerCase();
          const slug = (m.slug || '').toLowerCase();
          return name.includes(q) || slug.includes(q);
        })
      );
    },

    hideFilterPopup() {
      if (this.$refs.filterDropdown?.isFocusElement?.()) return;
      if (this.preselectedType) {
        EventBus.$emit('manualHidePopup');
        return;
      }
      this.$emit('hideFilterPopup');
    },

    selectNewFilterType(filterType) {
      this.selectedFilterType = filterType.key;
      this.mergeFilters();
      if (
        ['has_members', 'currentuser', 'is_parent', 'is_child', 'is_archived'].includes(
          filterType.key
        )
      ) {
        this.filterItemClick(filterType);
      }

      this.focusFilterInput();
    },

    multivalueFilterKey(key) {
      return [
        'creators',
        'members',
        'tags',
        'due_date',
        'time_created',
        'external_links',
        'statuses',
        'projects',
        'boards',
        'linked_cards',
        'parent_cards',
        'epics',
      ].includes(key);
    },

    focusFilterInput() {
      this.$refs.filterDropdown.focusInput();
    },

    selectSearchResult(selectedItem) {
      if (this.selectedFilterType || (this.searchValue && !selectedItem.category)) {
        this.filterItemClick(selectedItem);
      } else {
        this.selectNewFilterType(selectedItem);
      }
    },

    itemRightSideClick(item) {
      if (['has_members', 'currentuser'].includes(item.key)) {
        this.selectSearchResult(item);
        this.hideFilterPopup();
        return;
      }

      this.filterItemClick(item);
      this.hideFilterPopup();
    },

    selectOnlySearchResult(item) {
      if (['has_members', 'currentuser'].includes(item.key)) {
        this.selectSearchResult(item);
        return;
      }
      this.filterItemClick(item, false);
    },

    filterItemClick(item, closePopup = true) {
      switch (item.key) {
        case 'has_members':
        case 'currentuser':
          this.handleUserClick(item, 'members');
          this.selectedFilterType = '';
          this.mergeFilters();
          break;

        case 'members':
        case 'creators':
        case 'owners':
        case 'tags':
        case 'priority':
        case 'estimates':
        case 'projects':
        case 'boards':
        case 'statuses':
        case 'linked_cards':
        case 'parent_cards':
        case 'epics':
          this.handleUserClick(item, item.key);
          this.focusFilterInput();
          break;

        default:
          this.handleUserClick(item, item.key);
          if (closePopup) {
            this.hideFilterPopup();
          }
          break;
      }
    },

    handleUserClick(item, filterKey) {
      const filters = JSON.parse(JSON.stringify(this.currentFilters));

      if (this.handleSpecialFilterCases(filters, item, filterKey)) {
        return;
      }

      this.handleStandardFilters(filters, item, filterKey);
    },

    handleSpecialFilterCases(filters, item, filterKey) {
      // Title filter
      if (filterKey === 'title') {
        filters.include.title = item.value;
        this.setFilters({ filters });
        return true;
      }

      // Backlog status filter
      if (
        filterKey === 'statuses' &&
        item.id === 'backlog' &&
        (this.isViewOpen || this.isMyWorkOrMemberRoute)
      ) {
        this.toggleBacklogStatusFilter(filters);
        return true;
      }

      // Has members filter
      if (filterKey === 'members' && item.id === 'has_members') {
        this.toggleHasMembersFilter(filters);
        return true;
      }

      // Is parent/child filter
      if (this.isParentChildFilter(filterKey, item.id)) {
        this.toggleBooleanFilter(filters, filterKey);
        return true;
      }

      // Date filters
      if (['due_date', 'time_created', 'time_updated'].includes(filterKey)) {
        this.handleDateFilter(filters, item, filterKey);
        return true;
      }

      // Is card archived filter
      if (filterKey === 'is_archived' && item.id === 'is_archived') {
        this.toggleBooleanFilter(filters, filterKey);
        return true;
      }

      return false;
    },

    handleStandardFilters(filters, item, filterKey) {
      // Handle status with has_status
      if (filterKey === 'statuses' && filters?.has_status && !filters.exclude.statuses) {
        filters.exclude.statuses = [item.id];
        this.setFilters({ filters });
        return;
      }

      // Handle members with has_members
      if (filterKey === 'members' && filters?.has_members && !filters.exclude.members) {
        filters.exclude.members = [item.id];
        this.setFilters({ filters });
        return;
      }

      // Handle excluded filters
      if (filters.exclude[filterKey]?.length) {
        this.toggleExcludedFilter(filters, item, filterKey);
        return;
      }

      // Handle included filters
      this.toggleIncludedFilter(filters, item, filterKey);
    },

    toggleFilterProperty(filters, property, excludeKey) {
      if (filters[property] === undefined) {
        filters[property] = !!(filters?.exclude?.[excludeKey] || []).length;
      } else {
        delete filters[property];
      }
      this.setFilters({ filters });
    },

    // Helper methods
    toggleBacklogStatusFilter(filters) {
      this.toggleFilterProperty(filters, 'has_status', 'statuses');
    },

    toggleHasMembersFilter(filters) {
      this.toggleFilterProperty(filters, 'has_members', 'members');
    },

    isParentChildFilter(filterKey, itemId) {
      return (
        (filterKey === 'is_parent' && itemId === 'is_parent') ||
        (filterKey === 'is_child' && itemId === 'is_child')
      );
    },

    toggleBooleanFilter(filters, filterKey) {
      filters[filterKey] = !filters[filterKey];
      if (!filters[filterKey]) {
        delete filters[filterKey];
      }
      this.setFilters({ filters });
      this.selectedFilterType = '';
      this.mergeFilters();
    },

    handleDateFilter(filters, item, filterKey) {
      filters[filterKey] = filters[filterKey] || {};

      if (filterKey === 'time_updated') {
        filters[filterKey].after = item.id;
      } else {
        if (item.id === '0' && this.isViewOpen && filterKey === 'due_date') {
          filters['has_due_date'] = false;
        } else {
          delete filters.has_due_date;
          filters[filterKey].before = item.id;
        }
      }

      this.processDateFilter(filters, item, filterKey);

      this.setFilters({ filters });
      this.selectedFilterType = '';
      this.mergeFilters();
    },

    processDateFilter(filters, item, filterKey) {
      if (filters[filterKey]?.after) {
        filters[filterKey].after = item.id;
      } else if (filters[filterKey]?.before === '0' || item.id === '0') {
        delete filters[filterKey];
        filters['has_due_date'] = false;
      } else {
        filters[filterKey].before = item.id;
      }
    },

    toggleExcludedFilter(filters, item, filterKey) {
      const itemExcludeIndex = filters.exclude[filterKey].findIndex((i) => i === item.id);
      if (itemExcludeIndex > -1) {
        filters.exclude[filterKey].splice(itemExcludeIndex, 1);
        if (!filters.exclude[filterKey].length) {
          delete filters.exclude[filterKey];
        }
      } else {
        filters.exclude[filterKey].push(item.id);
      }
      this.setFilters({ filters });
    },

    toggleIncludedFilter(filters, item, filterKey) {
      if (!filters.include[filterKey]?.length) {
        filters.include[filterKey] = [item.id];
      } else {
        const itemIndex = filters.include[filterKey].findIndex((i) => i === item.id);
        if (itemIndex > -1) {
          filters.include[filterKey].splice(itemIndex, 1);
          if (!filters.include[filterKey].length) {
            delete filters.include[filterKey];
          }
        } else {
          filters.include[filterKey].push(item.id);
        }
      }
      this.setFilters({ filters });
    },

    escHandler(event) {
      event?.stopPropagation();
      if (this.preselectedType) {
        this.hideFilterPopup();
        return;
      }
      if (this.selectedFilterType) {
        this.selectedFilterType = '';
        return;
      }
      this.hideFilterPopup();
    },

    setFilters({ boardId = this.currentBoardId, filters }) {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        this.setViewFilters(filters);
        return;
      }

      this.$store.dispatch('setBoardFilters', { boardId, filters });
    },

    getProjectBoards(projectId) {
      return this.$store.getters.getProjectBoards(projectId) || [];
    },

    mapDateFilters(filters, category, labelFn = undefined) {
      const labelFunction = typeof labelFn === 'function' ? labelFn : (x) => x;

      const values = filters.map((option) => ({
        key: category,
        id: option,
        label: this.translate(labelFunction(option)),
        lighter: true,
      }));
      if (!this.searchValue) {
        return values;
      }
      return values.filter((ft) => ft.label.toLowerCase().includes(this.searchValue.toLowerCase()));
    },

    getParentCardsForBoard() {
      const parentCards = [];
      const addedParentCardIds = new Set();

      const board = this.$store.getters.getBoardById(this.currentBoardId);
      if (board.lists && board.lists.length) {
        const boardLists = board.lists.map((list) => list.id);
        boardLists.forEach((listId) => {
          const listCards = this.$store.getters.getListCards[listId] || [];

          listCards.forEach((cardId) => {
            const card = this.$store.getters.getCardById(cardId);
            if (!card?.parent_card) return;

            const parentCardId = card.parent_card.card_id;
            if (!addedParentCardIds.has(parentCardId)) {
              parentCards.push(card.parent_card);
              addedParentCardIds.add(parentCardId);
            }
          });
        });
      }

      return parentCards;
    },

    getEpicsForBoard() {
      const epicCards = [];
      const addedEpicCardIds = new Set();

      const boardCards = this.$store.getters.getBoardsCardsIdsByBoardId(this.currentBoardId);
      boardCards.forEach((cardId) => {
        const card = this.$store.getters.getCardById(cardId);
        if (!card?.epic) return;

        const epicCardId = card.epic.id;
        if (!addedEpicCardIds.has(epicCardId)) {
          epicCards.push(card.epic);
          addedEpicCardIds.add(epicCardId);
        }
      });

      return epicCards;
    },

    getProjectsForEpic() {
      const projectsArray = [];
      const epicId = this.currentBoardId.split('-')[1] || '';

      if (!epicId) return projectsArray;

      const epicCards = Object.values(this.$store.getters.getCardsByEpicId(epicId)) || [];

      const addedProjectIds = new Set();
      epicCards.reduce((acc, card) => {
        const project = this.teamProjects.find((proj) => proj.id === card.project_id);

        if (project && !addedProjectIds.has(project.id)) {
          acc.push(project);
          addedProjectIds.add(project.id);
        }

        return acc;
      }, projectsArray);

      return projectsArray;
    },

    // returns board ids of cards in current epic
    getBoardsOfEpic() {
      const boardsSet = new Set();
      const epicId = this.currentBoardId.split('-')[1] || '';

      if (!epicId) return boardsSet;

      const epicCards = Object.values(this.$store.getters.getCardsByEpicId(epicId)) || [];

      epicCards.forEach((card) => {
        if (card.board_id) {
          boardsSet.add(card.board_id);
        }
      });

      return boardsSet;
    },

    debounceSearch(value) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debouncedSearchValue = value;
      }, 200);
    },
  },
};
</script>
